import { useState, type FormEvent } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { authApiClient } from "@/lib/auth";
import { useToast } from "@/hooks/use-toast";
import { Button, buttonClasses, FormField, inputClass } from "@/components/ui";

const MIN_PASSWORD_LENGTH = 8;
const RESET_TOKEN_RE = /^[a-f0-9]{64}$/;

interface ResetPasswordSearch {
  token?: string;
}

export const Route = createFileRoute("/portal/$slug/reset-password")({
  validateSearch: (search: Record<string, unknown>): ResetPasswordSearch => ({
    token:
      typeof search.token === "string" && RESET_TOKEN_RE.test(search.token)
        ? search.token
        : undefined,
  }),
  component: ResetPasswordPage,
});

/**
 * Sets a new password from the token the utility's reset email carries. The
 * confirm is the ONE main-stack op the customer stack does not fork: the
 * customer forgot-password request writes the shared reset-token table, so
 * this page confirms through the main `/auth/reset-password` op on
 * `authApiClient`, the sole sanctioned use of that client.
 */
function ResetPasswordPage() {
  const { slug } = Route.useParams();
  const { token } = Route.useSearch();
  const { t } = useTranslation(["customer", "common", "auth"]);
  const { apiError: showApiError } = useToast();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const resetMutation = useMutation({
    mutationFn: async (body: { token: string; new_password: string }) => {
      const { error } = await authApiClient.POST("/auth/reset-password", { body });
      if (error) throw error;
    },
    // The client throws the API's error envelope; only its code picks the toast.
    onError: (err: unknown) => showApiError((err as { code?: string })?.code),
  });

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (token === undefined || resetMutation.isPending) return;
    const formData = new FormData(e.currentTarget);
    const newPassword = String(formData.get("new_password") ?? "");
    const confirmPassword = String(formData.get("confirm_password") ?? "");

    const errors: Record<string, string> = {};
    if (!newPassword) {
      errors.new_password = t("common:validation.required");
    } else if (newPassword.length < MIN_PASSWORD_LENGTH) {
      errors.new_password = t("common:validation.minLength", { min: MIN_PASSWORD_LENGTH });
    }
    if (!confirmPassword) {
      errors.confirm_password = t("common:validation.required");
    } else if (newPassword && confirmPassword !== newPassword) {
      errors.confirm_password = t("auth:passwordMismatch");
    }
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    resetMutation.mutate({ token, new_password: newPassword });
  };

  const title = (
    <h2 className="text-xl font-semibold tracking-[-0.01em] text-ink">{t("portal.reset.title")}</h2>
  );

  if (token === undefined) {
    return (
      <div className="space-y-5">
        {title}
        <p className="text-sm text-body">{t("portal.reset.invalid")}</p>
        <Link
          to="/portal/$slug/forgot-password"
          params={{ slug }}
          className={`${buttonClasses("primary")} w-full`}
        >
          {t("portal.reset.requestNew")}
        </Link>
      </div>
    );
  }

  if (resetMutation.isSuccess) {
    return (
      <div className="space-y-5">
        {title}
        <p role="status" className="text-sm text-body">
          {t("portal.reset.done")}
        </p>
        <Link to="/portal/$slug" params={{ slug }} className={`${buttonClasses("primary")} w-full`}>
          {t("portal.forgot.back")}
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {title}
      <form className="space-y-4" onSubmit={handleSubmit} noValidate>
        <FormField label={t("portal.reset.password")} error={fieldErrors.new_password}>
          <input
            name="new_password"
            type="password"
            autoComplete="new-password"
            className={inputClass}
          />
        </FormField>
        <FormField label={t("portal.reset.confirm")} error={fieldErrors.confirm_password}>
          <input
            name="confirm_password"
            type="password"
            autoComplete="new-password"
            className={inputClass}
          />
        </FormField>
        <Button type="submit" className="w-full" disabled={resetMutation.isPending}>
          {resetMutation.isPending ? t("portal.reset.submitting") : t("portal.reset.submit")}
        </Button>
      </form>
      <Link to="/portal/$slug" params={{ slug }} className={buttonClasses("link", "sm")}>
        {t("portal.forgot.back")}
      </Link>
    </div>
  );
}
